import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Plus, Trash2, Flame, Check } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function localDateStr(d) { return [d.getFullYear(),String(d.getMonth()+1).padStart(2,'0'),String(d.getDate()).padStart(2,'0')].join('-'); }
function getMonday(d) { const dt=new Date(d); dt.setDate(dt.getDate()-dt.getDay()+(dt.getDay()===0?-6:1)); return localDateStr(dt); }
function addDays(ds,n) { const d=new Date(ds+"T12:00:00"); d.setDate(d.getDate()+n); return localDateStr(d); }
function formatShort(ds) { return new Date(ds+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"}); }

export default function HabitTracker() {
  const [tab, setTab] = useState("tracker");
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); return localDateStr(new Date(d.getFullYear(), d.getMonth(), 1)); });
  const [monthLogs, setMonthLogs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDays, setNewDays] = useState(null); // null = every day
  const [editHabit, setEditHabit] = useState(null);
  const confirm = useConfirm();

  const weekDates = Array.from({length:7},(_,i)=>addDays(weekStart,i));
  const weekEnd = weekDates[6];

  const fetchData = useCallback(async () => {
    try {
      const [h, l] = await Promise.all([
        API.get("/habits"),
        API.get(`/habits/logs?start=${weekStart}&end=${weekEnd}`),
      ]);
      setHabits(h.data);
      setLogs(l.data);
    } catch {}
  }, [weekStart, weekEnd]);

  const fetchStats = useCallback(async () => {
    try { const r = await API.get("/habits/stats?weeks=8"); setStats(r.data); } catch {}
  }, []);

  // Month helpers
  const monthYear = new Date(monthDate + "T12:00:00");
  const monthName = monthYear.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const daysInMonth = new Date(monthYear.getFullYear(), monthYear.getMonth() + 1, 0).getDate();
  const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(monthYear.getFullYear(), monthYear.getMonth(), i + 1);
    return localDateStr(d);
  });
  const monthEnd = monthDates[monthDates.length - 1];

  const fetchMonthLogs = useCallback(async () => {
    if (tab !== "monthly") return;
    try { const r = await API.get(`/habits/logs?start=${monthDate}&end=${monthEnd}`); setMonthLogs(r.data); } catch {}
  }, [tab, monthDate, monthEnd]);

  const prevMonth = () => { const d = new Date(monthDate + "T12:00:00"); d.setMonth(d.getMonth() - 1); setMonthDate(localDateStr(d)); };
  const nextMonth = () => { const d = new Date(monthDate + "T12:00:00"); d.setMonth(d.getMonth() + 1); setMonthDate(localDateStr(d)); };
  const goThisMonth = () => { const d = new Date(); setMonthDate(localDateStr(new Date(d.getFullYear(), d.getMonth(), 1))); };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (tab === "stats") fetchStats(); }, [tab, fetchStats]);
  useEffect(() => { fetchMonthLogs(); if (tab === "monthly" && habits.length === 0) API.get("/habits").then(r => setHabits(r.data)).catch(() => {}); }, [fetchMonthLogs, tab]);

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const goThisWeek = () => setWeekStart(getMonday(new Date()));

  const logSet = new Set(logs.map(l => l.habit_id + "|" + l.date));
  const monthLogSet = new Set(monthLogs.map(l => l.habit_id + "|" + l.date));
  const isDone = (hid, date) => logSet.has(hid + "|" + date);
  const isMonthDone = (hid, date) => monthLogSet.has(hid + "|" + date);

  const toggleLog = async (hid, date, currentDone) => {
    await API.post("/habits/log", { habit_id: hid, date, done: !currentDone });
    fetchData(); fetchMonthLogs();
  };

  const isApplicable = (habit, date) => {
    if (!habit.active_days) return true;
    const dow = new Date(date + "T12:00:00").getDay();
    const mapped = dow === 0 ? 6 : dow - 1; // JS Sun=0→6, Mon=1→0
    return habit.active_days.includes(mapped);
  };

  const createHabit = async () => {
    if (!newName.trim()) return;
    await API.post("/habits", { name: newName, active_days: newDays });
    toast.success("Habit created");
    setNewName(""); setNewDays(null); setShowCreate(false);
    fetchData();
  };

  const saveEdit = async () => {
    if (!editHabit) return;
    await API.put("/habits/" + editHabit.habit_id, { name: editHabit.name, active_days: editHabit.active_days });
    toast.success("Habit updated");
    setEditHabit(null); fetchData();
  };

  const deleteHabit = async (hid) => {
    if (!(await confirm("Delete Habit", "This will delete this habit and all its history."))) return;
    await API.delete("/habits/" + hid);
    fetchData(); fetchStats();
  };

  const today = localDateStr(new Date());

  return (
    <div data-testid="habit-tracker-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-[28px] font-bold text-[#37352F]">Habits</h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-8 bg-[#E8E8E6] rounded-[4px] p-0.5">
            <TabsTrigger value="tracker" className="text-[12px] h-7 px-3 rounded-[3px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Weekly</TabsTrigger>
            <TabsTrigger value="monthly" className="text-[12px] h-7 px-3 rounded-[3px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Monthly</TabsTrigger>
            <TabsTrigger value="stats" className="text-[12px] h-7 px-3 rounded-[3px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Stats</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <p className="text-[13px] text-[#6B6B6B] mb-4">Build consistency, track streaks</p>

      {tab === "tracker" && (<>
        {/* Week nav */}
        <div className="flex items-center gap-3 mb-4">
          <Button onClick={prevWeek} variant="ghost" size="icon" className="text-[#6B6B6B] h-7 w-7"><ChevronLeft className="w-4 h-4"/></Button>
          <span className="text-[14px] font-medium text-[#37352F] min-w-[200px] text-center">{formatShort(weekStart)} — {formatShort(weekEnd)}</span>
          <Button onClick={nextWeek} variant="ghost" size="icon" className="text-[#6B6B6B] h-7 w-7"><ChevronRight className="w-4 h-4"/></Button>
          <button onClick={goThisWeek} className="text-[12px] text-[#2383E2] hover:underline">This week</button>
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[12px] h-7 ml-auto"><Plus className="w-3.5 h-3.5 mr-1"/> New Habit</Button>
        </div>

        {/* Grid */}
        {habits.length === 0 ? (
          <div className="text-center py-16"><p className="text-[#6B6B6B] text-[14px]">No habits yet. Create one to start tracking.</p></div>
        ) : (
          <div className="border border-[#E0E0DE] rounded-[4px] overflow-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F7F6F3] border-b border-[#E0E0DE]">
                  <th className="text-[11px] font-medium text-[#6B6B6B] text-left px-3 py-2 min-w-[180px]">Habit</th>
                  {weekDates.map((d,i) => {
                    const isToday = d === today;
                    return <th key={d} className={"text-[11px] font-medium text-center px-2 py-2 w-[60px] " + (isToday ? "text-[#2383E2]" : "text-[#6B6B6B]")}>
                      <div>{DAYS[i]}</div>
                      <div className="text-[10px] font-normal">{new Date(d+"T12:00:00").getDate()}</div>
                    </th>;
                  })}
                  <th className="w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {habits.map(h => {
                  const weekDone = weekDates.filter(d => isApplicable(h,d) && isDone(h.habit_id,d)).length;
                  const weekApplicable = weekDates.filter(d => isApplicable(h,d)).length;
                  return (
                    <tr key={h.habit_id} className="border-b border-[#E8E8E6] hover:bg-[#F7F6F3]/50">
                      <td className="px-3 py-2">
                        <button onClick={() => setEditHabit({...h})} className="text-[13px] font-medium text-[#37352F] hover:text-[#2383E2] hover:underline text-left">{h.name}</button>
                        <div className="text-[10px] text-[#6B6B6B]">
                          {h.active_days ? h.active_days.map(d => DAYS[d]).join(", ") : "Every day"}
                          {weekApplicable > 0 && <span className="ml-2">{weekDone}/{weekApplicable}</span>}
                        </div>
                      </td>
                      {weekDates.map(d => {
                        const applicable = isApplicable(h, d);
                        const done = isDone(h.habit_id, d);
                        const isToday = d === today;
                        return (
                          <td key={d} className="text-center px-2 py-2">
                            {applicable ? (
                              <button onClick={() => toggleLog(h.habit_id, d, done)}
                                className={"w-[28px] h-[28px] rounded-[4px] flex items-center justify-center mx-auto transition-colors " +
                                  (done ? "bg-[#2383E2] text-white" : isToday ? "border-2 border-[#2383E2] text-transparent hover:bg-[#2383E2]/10" : "border border-[#E0E0DE] text-transparent hover:bg-[#F7F6F3]")}>
                                <Check className="w-4 h-4" />
                              </button>
                            ) : (
                              <div className="w-[28px] h-[28px] rounded-[4px] bg-[#F7F6F3] mx-auto" />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-1">
                        <button onClick={() => deleteHabit(h.habit_id)} className="text-[#B8B6B0] hover:text-[#EB5757] p-1"><Trash2 className="w-3.5 h-3.5"/></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>)}


      {tab === "monthly" && (<>
        <div className="flex items-center gap-3 mb-4">
          <Button onClick={prevMonth} variant="ghost" size="icon" className="text-[#6B6B6B] h-7 w-7"><ChevronLeft className="w-4 h-4"/></Button>
          <span className="text-[14px] font-medium text-[#37352F] min-w-[160px] text-center">{monthName}</span>
          <Button onClick={nextMonth} variant="ghost" size="icon" className="text-[#6B6B6B] h-7 w-7"><ChevronRight className="w-4 h-4"/></Button>
          <button onClick={goThisMonth} className="text-[12px] text-[#2383E2] hover:underline">This month</button>
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[12px] h-7 ml-auto"><Plus className="w-3.5 h-3.5 mr-1"/> New Habit</Button>
        </div>
        {habits.length === 0 ? (
          <div className="text-center py-16"><p className="text-[#6B6B6B] text-[14px]">No habits yet.</p></div>
        ) : (
          <div className="border border-[#E0E0DE] rounded-[4px] overflow-auto">
            <table className="w-full" style={{ minWidth: 200 + daysInMonth * 32 }}>
              <thead>
                <tr className="bg-[#F7F6F3] border-b border-[#E0E0DE]">
                  <th className="text-[11px] font-medium text-[#6B6B6B] text-left px-3 py-2 sticky left-0 bg-[#F7F6F3] z-10 min-w-[160px]">Habit</th>
                  {monthDates.map(d => {
                    const dt = new Date(d + "T12:00:00");
                    const dow = dt.getDay();
                    const isToday2 = d === today;
                    const isSun = dow === 0; const isSat = dow === 6;
                    return <th key={d} className={"text-center px-0 py-1 w-[30px] " + (isToday2 ? "text-[#2383E2]" : isSun || isSat ? "text-[#B8B6B0]" : "text-[#6B6B6B]")}>
                      <div className="text-[9px] font-medium">{DAYS[dow === 0 ? 6 : dow - 1].charAt(0)}</div>
                      <div className="text-[10px] font-normal">{dt.getDate()}</div>
                    </th>;
                  })}
                  <th className="w-[24px] sticky right-0 bg-[#F7F6F3] z-10 text-[10px] text-[#6B6B6B] font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {habits.map(h => {
                  const applicable = monthDates.filter(d => isApplicable(h, d));
                  const done = applicable.filter(d => isMonthDone(h.habit_id, d));
                  const pct = applicable.length > 0 ? Math.round(done.length / applicable.length * 100) : 0;
                  return (
                    <tr key={h.habit_id} className="border-b border-[#E8E8E6] hover:bg-[#F7F6F3]/30">
                      <td className="px-3 py-1.5 sticky left-0 bg-white z-10">
                        <span className="text-[12px] font-medium text-[#37352F]">{h.name}</span>
                      </td>
                      {monthDates.map(d => {
                        const appl = isApplicable(h, d);
                        const dn = isMonthDone(h.habit_id, d);
                        const isToday2 = d === today;
                        return (
                          <td key={d} className="text-center px-0 py-1">
                            {appl ? (
                              <button onClick={() => toggleLog(h.habit_id, d, dn)}
                                className={"w-[22px] h-[22px] rounded-[3px] flex items-center justify-center mx-auto transition-colors " +
                                  (dn ? "bg-[#2383E2] text-white" : isToday2 ? "border-2 border-[#2383E2] text-transparent" : "border border-[#E0E0DE] text-transparent hover:bg-[#F7F6F3]")}>
                                <Check className="w-3 h-3" />
                              </button>
                            ) : (
                              <div className="w-[22px] h-[22px] rounded-[3px] bg-[#F7F6F3] mx-auto" />
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center px-1 sticky right-0 bg-white z-10">
                        <span className={"text-[11px] font-medium " + (pct >= 80 ? "text-[#2383E2]" : pct >= 50 ? "text-[#6B6B6B]" : "text-[#EB5757]")}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>)}


      {tab === "stats" && (<>
        {stats.length === 0 ? (
          <div className="text-center py-16"><p className="text-[#6B6B6B] text-[14px]">No habit data yet.</p></div>
        ) : (
          <div className="space-y-3">
            {stats.map(s => (
              <div key={s.habit_id} className="border border-[#E0E0DE] rounded-[4px] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-[15px] font-medium text-[#37352F]">{s.name}</h3>
                    <p className="text-[11px] text-[#6B6B6B]">{s.active_days ? s.active_days.map(d => DAYS[d]).join(", ") : "Every day"}</p>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div><p className="text-[22px] font-bold text-[#2383E2]">{s.completion_rate}%</p><p className="text-[10px] text-[#6B6B6B]">Completion</p></div>
                    <div><p className="text-[22px] font-bold text-[#37352F] flex items-center gap-1"><Flame className="w-5 h-5 text-[#E6A23C]"/>{s.current_streak}</p><p className="text-[10px] text-[#6B6B6B]">Streak</p></div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-[6px] bg-[#E0E0DE] rounded-full">
                    <div className="h-full bg-[#2383E2] rounded-full transition-all" style={{width: s.completion_rate + "%"}} />
                  </div>
                  <span className="text-[11px] text-[#6B6B6B] shrink-0">{s.total_done}/{s.total_applicable} days</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </>)}

      {/* Create habit */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-[#E0E0DE] shadow-sm">
          <DialogHeader><DialogTitle className="text-[15px] font-semibold text-[#37352F]">New Habit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px] text-[#6B6B6B]">Name</Label><Input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&createHabit()} className="mt-1 bg-transparent border-[#E0E0DE] text-[14px]" autoFocus/></div>
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">Active Days</Label>
              <div className="flex gap-1 mt-1">
                <button onClick={()=>setNewDays(null)} className={"text-[11px] px-2 py-1 rounded-[3px] border transition-colors " + (!newDays ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>Every day</button>
                {DAYS.map((d,i)=>(<button key={i} onClick={()=>{const cur=newDays||[]; setNewDays(cur.includes(i)?cur.filter(x=>x!==i):[...cur,i]);}}
                  className={"text-[11px] w-[34px] py-1 rounded-[3px] border transition-colors " + ((newDays||[]).includes(i) ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>{d}</button>))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={createHabit} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit habit */}
      <Dialog open={!!editHabit} onOpenChange={v=>{if(!v)setEditHabit(null);}}>
        <DialogContent className="bg-white border-[#E0E0DE] shadow-sm">
          <DialogHeader><DialogTitle className="text-[15px] font-semibold text-[#37352F]">Edit Habit</DialogTitle></DialogHeader>
          {editHabit && <div className="space-y-3">
            <div><Label className="text-[12px] text-[#6B6B6B]">Name</Label><Input value={editHabit.name} onChange={e=>setEditHabit({...editHabit,name:e.target.value})} className="mt-1 bg-transparent border-[#E0E0DE] text-[14px]"/></div>
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">Active Days</Label>
              <div className="flex gap-1 mt-1">
                <button onClick={()=>setEditHabit({...editHabit,active_days:null})} className={"text-[11px] px-2 py-1 rounded-[3px] border transition-colors " + (!editHabit.active_days ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>Every day</button>
                {DAYS.map((d,i)=>(<button key={i} onClick={()=>{const cur=editHabit.active_days||[]; setEditHabit({...editHabit,active_days:cur.includes(i)?cur.filter(x=>x!==i):[...cur,i]});}}
                  className={"text-[11px] w-[34px] py-1 rounded-[3px] border transition-colors " + ((editHabit.active_days||[]).includes(i) ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>{d}</button>))}
              </div>
            </div>
          </div>}
          <DialogFooter><Button onClick={saveEdit} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
